/**
 * Centralized Firebase Cloud Functions registry and timeout tiers.
 *
 * `CLOUD_FUNCTIONS` maps a logical UPPER_SNAKE_CASE identifier to the
 * actual deployed callable name. Use it everywhere instead of inline
 * string literals so renaming a function in `functions/src/index.ts`
 * is a single edit.
 *
 * `CALLABLE_TIMEOUTS` collapses the three timeout tiers in use into
 * named constants. Most heavy generation operations (full DOCX
 * generators, multi-section LLM pipelines) need the LONG tier so they
 * don't trip the 60s default before the Cloud Function itself bails.
 */

export const CLOUD_FUNCTIONS = {
  // ── Auth / admin ──────────────────────────────────────────────────
  // SETUP_ADMIN removed (security remediation C3) — the setupAdmin backdoor
  // callable was deleted from the backend.
  // Password reset via our own mailer + own-domain /reset-password link.
  REQUEST_PASSWORD_RESET: "requestPasswordReset",
  /**
   * P1 error capture. UNAUTHENTICATED-tolerant by design — an error that
   * happens before sign-in is exactly the one nobody would otherwise hear
   * about. Called only from `features/errors/`.
   */
  REPORT_CLIENT_ERROR: "reportClientErrorV2",
  /** P1 step 9: admin-only review and judgement of captured errors. */
  LIST_PLATFORM_ERRORS: "listPlatformErrorsV2",
  ACKNOWLEDGE_PLATFORM_ERROR: "acknowledgePlatformErrorV2",
  GET_USERS: "getUsersV2",
  UPDATE_USER_STATUS: "updateUserStatusV2",
  SET_PROJECT_LIMIT: "setProjectLimitV2",
  CREATE_MANAGED_USER: "createManagedUserV2",
  UPDATE_MANAGED_USER_ROLE: "updateManagedUserRoleV2",
  DELETE_MANAGED_USER: "deleteManagedUserV2",
  COMPLETE_INITIAL_PASSWORD_CHANGE: "completeInitialPasswordChangeV2",
  UPDATE_MANAGED_USER_MFA_REQUIREMENT: "updateManagedUserMfaRequirementV2",
  UPDATE_MANAGED_USER_SUPER_ADMIN: "updateManagedUserSuperAdminV2",
  COMPLETE_MFA_ENROLLMENT: "completeMfaEnrollmentV2",
  LIST_SSO_PROVIDERS: "listSsoProvidersV2",
  LIST_SSO_PROVIDERS_ADMIN: "listSsoProvidersAdminV2",
  SAVE_SSO_PROVIDER: "saveSsoProviderV2",
  DELETE_SSO_PROVIDER: "deleteSsoProviderV2",

  // ── Project lifecycle ─────────────────────────────────────────────
  CREATE_PROJECT: "createProjectV2",
  SAVE_STUDY_SETTINGS: "saveStudySettingsV2",
  /** Soft-delete: move a project to the archive (owner or admin). */
  ARCHIVE_PROJECT: "archiveProject",
  /** Restore an archived project (admin only). */
  RESTORE_PROJECT: "restoreProject",
  /** Recursive + audited permanent delete (admin only). */
  HARD_DELETE_PROJECT: "hardDeleteProject",
  /** Creator-only recursive delete for a failed-upload stub. */
  DISCARD_FAILED_PROJECT: "discardFailedProject",
  /** Admin archive page — list archived projects. */
  LIST_ARCHIVED_PROJECTS: "listArchivedProjects",
  /** Admin archive page — recent deletion audit feed. */
  LIST_DELETION_AUDIT: "listDeletionAudit",

  // ── Membership ────────────────────────────────────────────────────
  // Routed to the lean `core` codebase (functions-core) build of these
  // callables. Same logic; startup memory independent of the shared bundle.
  INVITE_MEMBER: "inviteMemberV2Core",
  ACCEPT_INVITATION: "acceptInvitationV2Core",
  REVOKE_INVITATION: "revokeInvitationV2Core",
  GET_INVITATION: "getInvitationV2Core",
  LIST_MY_PROJECTS: "listMyProjectsV2Core",
  UPDATE_MEMBER: "updateMemberV2Core",
  REMOVE_MEMBER: "removeMemberV2Core",
  TRANSFER_OWNERSHIP: "transferOwnershipV2Core",

  // ── Organizations (multi-tenant org wall) ────────────────────────
  /** Platform admin — create an org; seeds/invites its first orgAdmin. */
  PROVISION_ORGANIZATION: "provisionOrganization",
  /** Platform admin or that org's orgAdmin — (re)issue an org-admin invite. */
  INVITE_ORG_ADMIN: "inviteOrgAdmin",
  GET_ORG_INVITATION: "getOrgInvitation",
  ACCEPT_ORG_INVITATION: "acceptOrgInvitation",
  /** Platform admin — org inventory (rules deny client list on organizations). */
  LIST_ORGANIZATIONS: "listOrganizations",
  /** Platform admin: place an approved account that has no organisation (All Users fix-up). */
  ASSIGN_ACCOUNT_ORGANIZATION: "assignAccountOrganizationV2",
  /** Platform admin or that org's orgAdmin — Tier-1 branding (name/logo/colors). */
  UPDATE_ORG_BRANDING: "updateOrgBranding",

  // ── Sites (Phase 2) ───────────────────────────────────────────────
  GET_SITES: "getSitesV2",
  CREATE_SITE: "createSiteV2",
  UPDATE_SITE: "updateSiteV2",
  DELETE_SITE: "deleteSiteV2",
  UPDATE_SITE_STATUS: "updateSiteStatusV2",

  // ── Subjects (Phase 2) ────────────────────────────────────────────

  // ── TMF / essential docs ──────────────────────────────────────────
  INITIALIZE_TMF: "initializeTMFV2",
  GET_TMF_SUMMARY: "getTMFSummaryV2",
  GET_ESSENTIAL_DOCS: "getEssentialDocsV2",
  UPDATE_ESSENTIAL_DOC_STATUS: "updateEssentialDocStatusV2",

  // ── Protocol / requirements ───────────────────────────────────────
  EXTRACT_REQUIREMENTS: "extractRequirementsV2",
  /**
   * Compare an uploaded amendment against the protocol version the study
   * currently holds. Takes `{projectId, amendmentFilePath, documentKind}` — the
   * kind is REQUIRED and unrecognised values are refused, never defaulted.
   * Downloads and parses up to two protocol PDFs and makes one model call, at
   * `timeoutSeconds: 300` — use with `CALLABLE_TIMEOUTS.MEDIUM`.
   */
  COMPARE_PROTOCOLS: "compareProtocolsV2",
  /**
   * Adopt an amendment: archive the outgoing version, file the new one in the
   * trial folder, record what the comparison flagged. Gated on `approve`, not
   * `generate`. Makes no model call — the browser default timeout is fine.
   */
  ADOPT_AMENDMENT: "adoptAmendmentV2",
  // ── Chat / orchestration ──────────────────────────────────────────
  CHAT: "chatV2",
  GENERATE_ARTIFACT: "generateArtifactV2",
  PLAN_ARTIFACTS: "planArtifactsV2",

  // ── Consent ───────────────────────────────────────────────────────
  GENERATE_CONSENT_SECTION: "generateConsentSectionV2",
  SAVE_CONSENT_SECTION_DRAFT: "saveConsentSectionDraftV2",
  APPROVE_CONSENT_SECTION: "approveConsentSectionV2",
  INITIALIZE_CONSENT_BUILDER: "initializeConsentBuilderV2",
  NAVIGATE_TO_SECTION: "navigateToSectionV2",

  // ── CRF Builder ───────────────────────────────────────────────────
  SAVE_CRF_FORM: "saveCrfFormV2",
  IMPORT_REFERENCE_FORM: "importReferenceFormV2",
  LIST_CRF_FORMS: "listCrfFormsV2",
  RECONCILE_CRF_ARTIFACTS: "reconcileCrfArtifactsV2",
  SUGGEST_CRF_FORMS: "suggestCRFFormsV2",
  GENERATE_CRF_VARIABLES: "generateCRFVariablesV2",
  /** Compares each CRF form with the protocol and lists mismatches (flag only). Use with `CALLABLE_TIMEOUTS.GENERATOR_XLARGE`; the result is read from Firestore, not the reply. */
  REVIEW_CRF_AGAINST_PROTOCOL: "reviewCrfAgainstProtocolV2",
  EXPORT_CRF_TO_DOCX: "exportCRFToDocxV2",
  EXPORT_ALL_CRFS_TO_DOCX: "exportAllCRFsToDocxV2",
  /** Attach a user's PDF or Word file as a form's page rendering (Word is
   *  converted server-side). `timeoutSeconds: 300` — use with
   *  `CALLABLE_TIMEOUTS.MEDIUM`. */
  ATTACH_CRF_RENDERING: "attachCrfRenderingV2",
  /** Every CRF as one archive — Word or PDF, one file per form. */
  EXPORT_ALL_CRFS_AS_ZIP: "exportAllCRFsAsZipV2",
  EXPORT_ANNOTATED_CRF_TO_DOCX: "exportAnnotatedCRFToDocxV2",
  EXPORT_ANNOTATED_CRF_TO_PDF: "exportAnnotatedCRFToPdfV2",
  /**
   * Whole-project annotated CRF. Unlike EXPORT_ANNOTATED_CRF_TO_* above, which
   * take a single form's variables from the client, these build one aCRF
   * covering every form in the study, server-side. Generate writes the PDF to
   * dataManagement/acrf; download returns a signed URL for it.
   */
  GENERATE_ANNOTATED_CRF: "generateAnnotatedCRFV2",
  // Rename notice buttons: preview / update field names only / dismiss backstop.
  RESOLVE_RENAME_NOTICE: "resolveRenameNoticeV2",
  DOWNLOAD_ANNOTATED_CRF: "downloadAnnotatedCRFV2",
  GET_CRF_SOURCE_URL: "getCrfSourceUrlV2",
  EXTRACT_CRF_PACKET: "extractCrfPacketV2",
  /** Extract an uploaded visit schedule into studyDesign/config.visits. */
  EXTRACT_VISIT_SCHEDULE: "extractVisitScheduleV2",
  /** Strict-match schedule labels to the study's real CRF forms (no model call). */
  MATCH_SCHEDULE_FORMS: "matchScheduleFormsV2",
  IMPORT_REDCAP_DATA_DICTIONARY: "importRedcapDataDictionaryV2",
  /** Assembles all CRF forms assigned to a visit into a single printable PDF. */
  GENERATE_VISIT_PACKET: "generateVisitPacketV2",

  // ── CDISC Controlled Terminology ──────────────────────────────────
  LIST_CT_CODELISTS: "listCtCodelistsV2",

  // ── Data Management ───────────────────────────────────────────────
  GENERATE_DATA_MANAGEMENT_PLAN: "generateDataManagementPlanV2",
  /** Gen-2 V2 — flipped in client-flip 2 of the 2026-05-23 migration.
   *  Use with `CALLABLE_TIMEOUTS.GENERATOR_XLARGE` (1800s server / 31 min client). */
  GENERATE_COMPREHENSIVE_DMP_V2: "generateComprehensiveDMPV2",
  REGENERATE_DMP_SECTION: "regenerateDMPSectionV2",
  EXPORT_DMP_DOCX: "exportDMPDocxV2",
  /** Gen-2 V2 of the spec DD generator. Wave 2 of the 2026-05-22 migration
   *  flipped the primary call sites (DataManagementHub, CDMArchitectureDiagram)
   *  to this endpoint. Use with `CALLABLE_TIMEOUTS.GENERATOR_XLARGE`. */
  GENERATE_SPEC_DATA_DICTIONARY_V2: "generateSpecDataDictionaryV2",
  DOWNLOAD_DATA_DICTIONARY: "downloadDataDictionaryV2",
  // Round trip: apply edits made to THIS study's dictionary export. Not to be
  // confused with IMPORT_REDCAP_DATA_DICTIONARY, which creates forms from a
  // REDCap file.
  PLAN_DICTIONARY_IMPORT: "planDictionaryImportV2",
  APPLY_DICTIONARY_IMPORT: "applyDictionaryImportV2",
  // Adopt a FOREIGN dictionary as the source of truth for draft forms. Only
  // offered while nothing downstream is keyed on the variable names yet.
  PLAN_DICTIONARY_ADOPTION: "planDictionaryAdoptionV2",
  APPLY_DICTIONARY_ADOPTION: "applyDictionaryAdoptionV2",
  DICTIONARY_ADOPTION_ELIGIBILITY: "getDictionaryAdoptionEligibilityV2",
  /** Gen-2 V2 — flipped in client-flip 2. Use with GENERATOR_XLARGE. */
  GENERATE_EDIT_CHECK_SPECIFICATION_V2: "generateEditCheckSpecificationV2",
  EXPORT_EDIT_CHECK_SPEC_CSV: "exportEditCheckSpecCsvV2",
  EXPORT_EDIT_CHECK_SPEC_DOCX: "exportEditCheckSpecDocxV2",
  EXPORT_EDIT_CHECKS: "exportEditChecksV2",
  /** Gen-2 V2 — flipped in client-flip 2. Use with GENERATOR_XLARGE. */
  GENERATE_CRF_INSTRUCTION_GUIDE_V2: "generateCRFInstructionGuideV2",
  /** Starts the CRF Instruction Guide as a background job. Returns
   *  `{ jobId }` on success, or a structured precondition payload
   *  ({ error: "CRF_RENDERINGS_MISSING" | "CRF_RENDERINGS_DUPLICATE", ... }),
   *  or THROWS an HttpsError (no forms / a job already running). The job's
   *  progress is published to `dataManagement/crfGuideJob` (pointer) and
   *  `guideJobs/{jobId}` (status doc), both subscribed via onSnapshot. The
   *  call itself returns quickly, so the browser default timeout is fine. */
  START_CRF_GUIDE_JOB: "startCrfGuideJob",
  DOWNLOAD_CRF_INSTRUCTION_GUIDE: "downloadCRFInstructionGuideV2",

  // ── Visit / form matrix ───────────────────────────────────────────
  /** Schedule-of-Assessments export (format: 'csv' | 'odm' | 'docx'). */
  EXPORT_VISIT_FORM_MATRIX_V2: "exportVisitFormMatrixV2",

  // ── SDTM ──────────────────────────────────────────────────────────
  /** Gen-2 V2 — flipped in client-flip 2. Use with GENERATOR_XLARGE. */
  GENERATE_SDTM_MAPPING_V2: "generateSDTMMappingV2",

  // ── External / coding / acceptance ────────────────────────────────
  /** Gen-2 V2 — flipped in client-flip 3. Use with GENERATOR_XLARGE. */
  GENERATE_EDTS_V2: "generateEDTSV2",
  EXPORT_EDTS_DOCX: "exportEDTSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 3. Use with GENERATOR_XLARGE. */
  GENERATE_MCS_V2: "generateMCSV2",
  REGENERATE_MCS_SECTION: "regenerateMCSSectionV2",
  EXPORT_MCS_DOCX: "exportMCSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 3. Use with GENERATOR_XLARGE. */
  GENERATE_DATA_REVIEW_CLEANING_PLAN_V2: "generateDataReviewCleaningPlanV2",
  EXPORT_DRCP_DOCX: "exportDRCPDocxV2",
  /** Gen-2 V2 — flipped in client-flip 3. Use with GENERATOR_XLARGE. */
  GENERATE_SAE_RECONCILIATION_PROCEDURE_V2: "generateSaeReconciliationProcedureV2",
  EXPORT_SAERP_DOCX: "exportSAERPDocxV2",
  /** Gen-2 V2 — flipped in client-flip 3. Use with GENERATOR_XLARGE. */
  GENERATE_DATABASE_LOCK_CHECKLIST_V2: "generateDatabaseLockChecklistV2",
  EXPORT_DBLC_DOCX: "exportDBLCDocxV2",
  /** Gen-2 V2 — flipped in client-flip 6. Use with GENERATOR_XLARGE. */
  GENERATE_UAT_PROTOCOL_V2: "generateUATProtocolV2",
  EXPORT_UAT_PROTOCOL_DOCX: "exportUATProtocolDocxV2",
  /** Gen-2 V2 — flipped in client-flip 6. Use with GENERATOR_XLARGE. */
  GENERATE_UAT_REPORT_V2: "generateUATReportV2",
  EXPORT_UAT_REPORT_DOCX: "exportUATReportDocxV2",
  /** Gen-2 V2 — flipped in client-flip 6. Use with GENERATOR_XLARGE. */
  GENERATE_TEST_PROCEDURE_V2: "generateTestProcedureV2",
  EXPORT_TEST_PROCEDURE_DOCX: "exportTestProcedureDocxV2",
  EXPORT_TEST_INCIDENT_REPORT_TEMPLATE_DOCX: "exportTestIncidentReportTemplateDocxV2",
  /** Gen-2 V2 — flipped in client-flip 6. Use with GENERATOR_XLARGE. */
  GENERATE_TEST_PLAN_V2: "generateTestPlanV2",
  EXPORT_TEST_PLAN_DOCX: "exportTestPlanDocxV2",
  GENERATE_TEST_LIST: "generateTestListV2",
  EXPORT_TEST_LIST_DOCX: "exportTestListDocxV2",
  EXPORT_TEST_REPORT_TEMPLATE_DOCX: "exportTestReportTemplateDocxV2",

  // ── Requirements & Design family ──────────────────────────────────
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_URS_V2: "generateURSV2",
  EXPORT_URS_DOCX: "exportURSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_FRS_V2: "generateFRSV2",
  EXPORT_FRS_DOCX: "exportFRSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_SDS_V2: "generateSDSV2",
  EXPORT_SDS_DOCX: "exportSDSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_ADS_V2: "generateADSV2",
  EXPORT_ADS_DOCX: "exportADSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_ATDS_V2: "generateATDSV2",
  EXPORT_ATDS_DOCX: "exportATDSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_SACS_V2: "generateSACSV2",
  EXPORT_SACS_DOCX: "exportSACSDocxV2",
  /** Gen-2 V2 — flipped in client-flip 4. Use with GENERATOR_XLARGE. */
  GENERATE_IIS_V2: "generateIISV2",
  EXPORT_IIS_DOCX: "exportIISDocxV2",

  // ── Atomic spec documents (CRCU template family) ──────────────────
  /** Gen-2 V2 — flipped in client-flip 5. Use with GENERATOR_XLARGE. */
  GENERATE_SYSTEM_REQUIREMENTS_V2: "generateSystemRequirementsV2",
  EXPORT_SYSTEM_REQUIREMENTS_DOCX: "exportSystemRequirementsDocxV2",
  /** Gen-2 V2 — flipped in client-flip 5. Use with GENERATOR_XLARGE. */
  GENERATE_SYSTEM_REQUIREMENTS_ATTRIBUTES_V2: "generateSystemRequirementsAttributesV2",
  EXPORT_SYSTEM_REQUIREMENTS_ATTRIBUTES_DOCX: "exportSystemRequirementsAttributesDocxV2",
  /** Gen-2 V2 — flipped in client-flip 5. Use with GENERATOR_XLARGE. */
  GENERATE_DB_PHYSICAL_MODEL_V2: "generateDbPhysicalModelV2",
  EXPORT_DB_PHYSICAL_MODEL_DOCX: "exportDbPhysicalModelDocxV2",
  /** Gen-2 V2 — flipped in client-flip 5. Use with GENERATOR_XLARGE. */
  GENERATE_DB_ATTRIBUTES_V2: "generateDbAttributesV2",
  EXPORT_DB_ATTRIBUTES_DOCX: "exportDbAttributesDocxV2",
  /** Gen-2 V2 — flipped in client-flip 5. Use with GENERATOR_XLARGE. */
  GENERATE_INTERFACE_FEATURES_V2: "generateInterfaceFeaturesV2",
  EXPORT_INTERFACE_FEATURES_DOCX: "exportInterfaceFeaturesDocxV2",
  /** Gen-2 V2 — flipped in client-flip 5. Use with GENERATOR_XLARGE. */
  GENERATE_INTERFACE_ATTRIBUTES_V2: "generateInterfaceAttributesV2",
  EXPORT_INTERFACE_ATTRIBUTES_DOCX: "exportInterfaceAttributesDocxV2",

  // ── Audit / quality ───────────────────────────────────────────────
  /** Activity-log read for the Activity page (paginated, view_activity-gated). */
  GET_AUDIT_LOGS: "getAuditLogsV2",
  /** Activity-log export — returns the full window for CSV download. */
  EXPORT_AUDIT_LOGS: "exportAuditLogsV2",
  // Legacy quality-audit / auto-repair / traceability-scorer callables removed
  // 2026-07-24 — the old system was retired; Verification is the single checker.
  /** Automatic Project Validation — manual "Re-run validation" (report only).
   *  Fast/deterministic; use with CALLABLE_TIMEOUTS.MEDIUM (matches the 300s
   *  server timeout). */
  RUN_VALIDATION_V2: "runValidationV2",
  /** Guide-quality validation (Wave 3) — its own trigger + on-demand. */
  RUN_GUIDE_VALIDATION_V2: "runGuideValidationV2",
  // Validation tab — approve an offered fix (existing guardrailed apply paths).
  APPLY_DD_FORM_NAME_RENAME_V2: "applyDdFormNameRenameV2",
  APPLY_DD_ORPHAN_REMOVAL_V2: "applyDdOrphanRemovalV2",
  APPLY_DUPLICATE_ARTIFACT_REMOVAL_V2: "applyDuplicateArtifactRemovalV2",
  // Validation tab — dismiss / undismiss a finding (records a reason).
  DISMISS_VALIDATION_FINDING_V2: "dismissValidationFindingV2",
  UNDISMISS_VALIDATION_FINDING_V2: "undismissValidationFindingV2",
  /** Cross-deliverable consistency check (staleness / count claims / DD↔SDTM).
   *  Records a run under pipelineVerification; read-level, no study data
   *  changed. Fast and deterministic — CALLABLE_TIMEOUTS.SHORT matches the
   *  120s server timeout. */
  RUN_PIPELINE_VERIFICATION_V2: "runPipelineVerificationV2",
  /** Needs-review queue — approve or decline an offered correction. Edit-gated;
   *  re-plans from live data server-side and can refuse
   *  ({applied:false, refusedReason}) per the approval contract. Use with
   *  CALLABLE_TIMEOUTS.MEDIUM (matches the 300s server timeout). */
  APPLY_CORRECTION_V2: "applyCorrectionV2",

  // ── Approval workflow (governance review gate) ────────────────────
  /** draft|in_review -> pending_approval; gated on the `edit` privilege. */
  SUBMIT_FOR_APPROVAL: "submitForApprovalV2",
  /** pending_approval -> approved|draft|in_review; gated on `approve`. */
  REVIEW_ARTIFACT: "reviewArtifactV2",
  CHECK_DOWNLOAD_ALLOWED: "checkDownloadAllowedV2",

  // ── Misc documents ────────────────────────────────────────────────
  /** Gen-2 V2 — flipped in client-flip 2. Use with GENERATOR_XLARGE
   *  when the caller previously used LONG; pass no timeout to use the
   *  browser default. */
  GENERATE_SPEC_DOCUMENT_V2: "generateSpecDocumentV2",

  // ── Document version control ──────────────────────────────────────
  ISSUE_VERSION: "issueVersion",
  GET_VERSION_DOWNLOAD_URL: "getVersionDownloadUrl",

  // ── Instruction library (admin curation) ─────────────────────────
  /** Paginated review worklist for harvested CRF completion instructions. */
  LIST_INSTRUCTION_LIBRARY: "listInstructionLibrary",
  /** Approve (client/global scope) or reject a single library entry. */
  REVIEW_INSTRUCTION: "reviewInstruction",
  /** Drafts one study-agnostic instruction from a cluster of per-client
   *  variants. Single model call — use with `CALLABLE_TIMEOUTS.SHORT`
   *  for headroom over the 60s browser default. */
  DRAFT_GENERALIZED_INSTRUCTION: "draftGeneralizedInstruction",
  /** Replaces a cluster of per-client entries with one global entry. */
  APPROVE_GENERALIZED_INSTRUCTION: "approveGeneralizedInstruction",
} as const;

export type CloudFunctionName =
  (typeof CLOUD_FUNCTIONS)[keyof typeof CLOUD_FUNCTIONS];

/**
 * Three tiers correspond to the three timeout values currently used in
 * the codebase: 2-minute (single-section regenerate), 5-minute (single
 * CRF generation, UAT export), and 9-minute (full document generation
 * and exports — the upper bound is the Cloud Function global timeout).
 */
export const CALLABLE_TIMEOUTS = {
  /** 2 min — single-section regeneration (regenerateDMPSection). */
  SHORT: 120_000,
  /** 5 min — single CRF generation, UAT DOCX export. */
  MEDIUM: 300_000,
  /** 9 min — full document / spec generation and the matching DOCX
   *  exports. Matches the Cloud Function's own 540s timeout ceiling. */
  LONG: 540_000,
  /**
   * 10 min — used for callables that run at the Cloud Function's 540s
   * ceiling. The 60s client-side buffer over the server ceiling avoids
   * a race where the browser SDK's deadline expires at the exact moment
   * the server times out, returning `deadline-exceeded` to the user even
   * when the function actually completed. Apply to long-running
   * generators like `generateSpecDataDictionary` that consistently push
   * close to the function timeout. (Added 2026-05-18 after generation
   * runs hitting 540s caused `deadline-exceeded` errors that led users
   * to re-click Generate and trigger overlapping invocations.)
   */
  XLONG: 600_000,
  /**
   * 31 min — Gen-2 extractor pace. `extractCrfPacket` migrated to a
   * Gen-2 Cloud Function with `timeoutSeconds: 1800` (30 min) in May
   * 2026 so dense CRF packets can subdivide and retry every failing
   * chunk inside Pass 1.5 / 1.6 instead of getting cut off at Gen-1's
   * 540s ceiling. The 60s client-side buffer over the server timeout
   * follows the same anti-race-with-server-deadline pattern as XLONG.
   */
  EXTRACTOR: 1_860_000,
  /**
   * 31 min — Gen-2 large-generator pace. Mirrors the EXTRACTOR pattern
   * for callables migrated to Gen-2 with `timeoutSeconds: 1800` in the
   * 2026-05-22 migration (wave 2 onward). First user:
   * `generateSpecDataDictionaryV2` — the heaviest DM-hub generator, which
   * had been pushing the XLONG (10 min) ceiling on large studies. The
   * 60s client-side buffer over the server timeout matches EXTRACTOR.
   */
  GENERATOR_XLARGE: 1_860_000,
} as const;

export type CallableTimeout =
  (typeof CALLABLE_TIMEOUTS)[keyof typeof CALLABLE_TIMEOUTS];
